// Shared visual language for the public/marketing surfaces.
// Console surfaces (Dashboard) use their own tighter tokens.
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
export const SURFACE_BORDER = '1px solid #DFE8EE';
export const SURFACE_SHADOW = '0 1px 2px rgba(11, 46, 67, 0.05), 0 16px 36px -18px rgba(11, 46, 67, 0.14)';
export const PANEL_RADIUS = '20px';
export const INNER_RADIUS = '14px';

export const sectionCardSx = {
  borderRadius: PANEL_RADIUS,
  border: SURFACE_BORDER,
  boxShadow: SURFACE_SHADOW,
  background: '#ffffff',
};

export const heroPanelSx = {
  borderRadius: '24px',
  border: '1px solid rgba(11, 46, 67, 0.08)',
  boxShadow: '0 2px 4px rgba(11, 46, 67, 0.04), 0 28px 64px -28px rgba(11, 46, 67, 0.18)',
  background:
    'radial-gradient(circle at 0% 0%, rgba(19,148,127,0.1), transparent 38%), radial-gradient(circle at 92% 12%, rgba(223,162,58,0.1), transparent 32%), linear-gradient(160deg, #ffffff 0%, #f4f9f8 60%, #fbfdfc 100%)',
};

export const darkPanelSx = {
  borderRadius: '24px',
  background:
    'radial-gradient(circle at 88% -10%, rgba(19,148,127,0.35), transparent 42%), radial-gradient(circle at -8% 110%, rgba(14,77,110,0.6), transparent 52%), linear-gradient(160deg, #0B2E43 0%, #103E59 100%)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 2px 4px rgba(11, 46, 67, 0.1), 0 32px 72px -28px rgba(11, 46, 67, 0.45)',
};

export const warmPanelSx = {
  borderRadius: '20px',
  border: '1px solid rgba(223, 162, 58, 0.3)',
  background: 'linear-gradient(170deg, #FDF8EC 0%, #FCFBF5 100%)',
  boxShadow: '0 1px 2px rgba(176, 122, 31, 0.05), 0 16px 36px -20px rgba(176, 122, 31, 0.18)',
};

export const insetPanelSx = {
  borderRadius: INNER_RADIUS,
  border: '1px solid #E6EDF2',
  backgroundColor: '#F7FAFC',
};

export const accentCardSx = (accent) => ({
  ...sectionCardSx,
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 220ms ease, box-shadow 220ms ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${accent}, transparent 80%)`,
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 2px 4px rgba(11, 46, 67, 0.05), 0 28px 56px -24px rgba(11, 46, 67, 0.22)',
  },
});

export const iconBadgeSx = (background, color) => ({
  width: 48,
  height: 48,
  display: 'grid',
  placeItems: 'center',
  borderRadius: '14px',
  background,
  color,
  flexShrink: 0,
});

export const eyebrowSx = {
  color: '#13947F',
  letterSpacing: '0.14em',
  fontWeight: 800,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
};

export const compactHeaderLabelSx = {
  color: '#13947F',
  letterSpacing: '0.08em',
  fontWeight: 800,
  fontSize: '0.74rem',
};

export const subtleCopySx = {
  color: '#52677A',
  lineHeight: 1.72,
};

// Standard page intro: eyebrow + display headline + supporting copy.
export const pageHeadlineSx = {
  color: '#0B2E43',
  fontSize: { xs: '2.1rem', md: '2.9rem' },
  letterSpacing: '-0.025em',
  lineHeight: 1.12,
};

export const checkBulletSx = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  bgcolor: 'rgba(19,148,127,0.13)',
  border: '1px solid rgba(19,148,127,0.25)',
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  mt: '2px',
};

export function CheckGlyph({ color = '#13947F' }) {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
      <polyline
        points="2 6 4.5 8.5 9 3"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Fades content up once it scrolls into view. Falls back to always-visible
// when IntersectionObserver is unavailable; prefers-reduced-motion collapses
// the transition via the global CSS override.
export function Reveal({ children, delay = 0, sx }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(24px)',
        transition: `opacity 650ms ease ${delay}ms, transform 650ms ease ${delay}ms`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function CrossGlyph({ color = '#C04531', size = 11, strokeWidth = 3 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
